import { c as _c } from "react/compiler-runtime";
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import type { AriaButtonProps } from '@react-aria/button';
import { useButton } from '@react-aria/button';
import { useFocusRing } from '@react-aria/focus';
import { mergeProps } from '@react-aria/utils';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import type { CalendarState, RangeCalendarState } from '@react-stately/calendar';
import { intlFormat } from 'date-fns/fp';
import type { HTMLAttributes } from 'react';
import { useRef } from 'react';
import { useLocale } from 'react-aria';
import { DEFAULT_TIME_ZONE } from '@/lib/time-zone';
import { cx } from '../../../cx';
import { uiTestId } from '../../../test-ids';
import { IconButton, type IconButtonProps } from '../../button/icon-button';
import { calendarDateToDate } from './to-calendar-date';
export type HeaderProps = {
  state: CalendarState | RangeCalendarState;
  calendarProps: HTMLAttributes<HTMLElement>;
  prevButtonProps: AriaButtonProps;
  nextButtonProps: AriaButtonProps;
  gridLabel?: (date: Date) => string;
};
export function Header(t0) {
  const $ = _c(29);
  const {
    state,
    calendarProps,
    prevButtonProps,
    nextButtonProps,
    gridLabel
  } = t0;
  const {
    locale
  } = useLocale();
  let t1;
  if ($[0] !== gridLabel || $[1] !== locale) {
    const formatIntl = intlFormat({
      locale
    });
    t1 = gridLabel || formatIntl({
      month: "long",
      year: "numeric",
      timeZone: DEFAULT_TIME_ZONE
    });
    $[0] = gridLabel;
    $[1] = locale;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const formatGridLabel = t1;
  const t2 = calendarProps["aria-label"];
  let t3;
  if ($[3] !== t2) {
    t3 = <VisuallyHidden><h5>{t2}</h5></VisuallyHidden>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = uiTestId("CalendarHeader.previous-months");
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = prevButtonProps["aria-label"] ?? "";
  let t6;
  if ($[6] !== prevButtonProps || $[7] !== t5) {
    t6 = <div className="z-10 col-span-1 col-start-1"><Button {...prevButtonProps} Icon={CaretLeft} data-testid={t4} aria-label={t5} /></div>;
    $[6] = prevButtonProps;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== formatGridLabel || $[10] !== state.visibleRange.start) {
    t7 = formatGridLabel(calendarDateToDate(state.visibleRange.start));
    $[9] = formatGridLabel;
    $[10] = state.visibleRange.start;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t7) {
    t8 = <Label className="col-start-1 sm:mr-3">{t7}</Label>;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== formatGridLabel || $[15] !== state.visibleRange.start) {
    t9 = formatGridLabel(calendarDateToDate(state.visibleRange.start.add({
      months: 1
    })));
    $[14] = formatGridLabel;
    $[15] = state.visibleRange.start;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t9) {
    t10 = <Label className="col-start-3 ml-3 hidden sm:block">{t9}</Label>;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = uiTestId("CalendarHeader.next-months");
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = nextButtonProps["aria-label"] ?? "";
  let t13;
  if ($[20] !== nextButtonProps || $[21] !== t12) {
    t13 = <div className="z-10 col-span-1 col-start-4 text-right"><Button {...nextButtonProps} Icon={CaretRight} data-testid={t11} aria-label={t12} /></div>;
    $[20] = nextButtonProps;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t10 || $[24] !== t13 || $[25] !== t3 || $[26] !== t6 || $[27] !== t8) {
    t14 = <div className="flex items-center px-4 pb-2 *:row-start-1 sm:grid">{t3}{t6}{t8}{t10}{t13}</div>;
    $[23] = t10;
    $[24] = t13;
    $[25] = t3;
    $[26] = t6;
    $[27] = t8;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  return t14;
}
function Label(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cx("col-span-2 flex-1 text-center font-heading5 font-semibold", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <h5 aria-hidden={true} className={t1}>{children}</h5>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function Button(t0) {
  const $ = _c(10);
  let Icon;
  let props;
  if ($[0] !== t0) {
    ({
      Icon,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = Icon;
    $[2] = props;
  } else {
    Icon = $[1];
    props = $[2];
  }
  const ref = useRef(null);
  const {
    buttonProps
  } = useButton(props, ref);
  const {
    focusProps
  } = useFocusRing();
  let t1;
  if ($[3] !== buttonProps || $[4] !== focusProps) {
    t1 = mergeProps(buttonProps, focusProps);
    $[3] = buttonProps;
    $[4] = focusProps;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const t2 = props["aria-label"] ?? "";
  let t3;
  if ($[6] !== Icon || $[7] !== t1 || $[8] !== t2) {
    t3 = <IconButton Icon={Icon} {...t1} ref={ref} aria-label={t2} />;
    $[6] = Icon;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}